import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import {
  useUpdateRepairScheduleMutation,
  useGetSchedulesByUserIDQuery,
  useGetPreventiveSchedulesByUserIDQuery,
  maintenanceApiSlice,
} from "../../slices/maintenanceApiSlice";
import {
  useGetUserByEmailQuery,
  useGetAllUsersQuery,
} from "../../slices/userApiSlice";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select as ShadSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import {
  Avatar as ShadAvatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import {
  Users,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Wrench,
  TrendingUp,
} from "lucide-react";
import Select from "react-select";
import { toast } from "sonner";

const SupervisorHome = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalData, setModalData] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedWorkStatus, setSelectedWorkStatus] = useState("all");
  const [rescheduleModalData, setRescheduleModalData] = useState(null);
  const [status, setStatus] = useState(null);

  const [assignTime, setAssignTime] = useState("");
  const [assignDate, setAssignDate] = useState("");
  const [selectedTechnicianId, setSelectedTechnicianId] = useState(null);
  const [selectedTechnicianUpdate, setSelectedTechnicianUpdate] =
    useState(null);

  const rowsPerPage = 10;

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(
    selectUserInfo,
    (userInfo) => userInfo?.username || "",
  );

  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  const userID = userByEmial?.user?.userId;
  const academyId = userByEmial?.user?.academyId;
  const departmentId = userByEmial?.user?.departmentId;

  const {
    data: usersResponse,
  } = useGetAllUsersQuery();

  const users = usersResponse?.users || usersResponse?.data || usersResponse || [];

  const filteredUsers = users?.filter((user) => {
    const matchesAcademy = !academyId || user.academyId === academyId;
    const matchesDepartment = !departmentId || user.departmentId === departmentId;

    const isTechnician =
      (typeof user.role?.name === "string" && user.role.name.toLowerCase().trim() === "technician") ||
      (typeof user.role === "string" && user.role.toLowerCase().trim() === "technician") ||
      (typeof user.roleName === "string" && user.roleName.toLowerCase().trim() === "technician");

    return matchesAcademy && matchesDepartment && isTechnician;
  });

  const workerOptions = filteredUsers?.map((user) => ({
    label: user.email,
    value: user.email,
  }));

  const {
    data: userSchedules,
    refetch,
  } = useGetSchedulesByUserIDQuery(userID, {
    skip: !userID,
  });

  const [data, setData] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRepairDetails = async () => {
      if (userSchedules && userSchedules.length) {
        const repairPromises = userSchedules.map(async (schedule) => {
          try {
            const repair = await dispatch(
              maintenanceApiSlice.endpoints.getRepairById.initiate(
                schedule?.repairID,
              ),
            ).unwrap();

            return {
              ...schedule,
              repairInfo: repair,
            };
          } catch (err) {
            console.error(`Error fetching repair ${schedule?.repairID}:`, err);
            return null;
          }
        });

        const repairResults = await Promise.all(repairPromises);
        const validData = repairResults.filter(Boolean);

        const sortedData = validData.sort((a, b) =>
          b.repairID.localeCompare(a.repairID),
        );

        setData(sortedData);
      }
    };

    fetchRepairDetails();
  }, [userSchedules, dispatch]);

  const [updateSchedule, { isLoading: updating }] =
    useUpdateRepairScheduleMutation();

  const handleSchedule = async () => {
    const repairID = modalData.repairID;
    if (!repairID) {
      toast.error("Error: Repair ID not found.");
      return;
    }
    const matchingSchedule = userSchedules.find(
      (schedule) => schedule.repairID === repairID,
    );

    if (!matchingSchedule) {
      toast.error("Error: No schedule found for the given repairID.");
      return;
    }

    const scheduleId = matchingSchedule.scheduleID;
    if (!selectedTechnicianId) {
      toast.error("Error: Technician email is missing.");
      return;
    }

    const updatedData = {
      technicianEmail: selectedTechnicianId,
      repairID: matchingSchedule.repairID,
    };

    try {
      await updateSchedule({ scheduleId, updatedData }).unwrap();
      toast.success("Scheduled successfully!");
      handleCloseModal();
    } catch (error) {
      toast.error(error?.data?.message || "Schedule failed");
    }
    refetch();
  };

  const handleReschedule = async () => {
    const repairID = rescheduleModalData.repairID;
    if (!repairID) {
      toast.error("Error: Repair ID not found.");
      return;
    }

    const matchingSchedule = userSchedules.find(
      (schedule) => schedule.repairID === repairID,
    );

    if (!matchingSchedule) {
      toast.error("Error: No schedule found for the given repairID.");
      return;
    }

    const scheduleId = matchingSchedule.scheduleID;
    if (!selectedTechnicianUpdate) {
      toast.error("Error: Technician email is missing.");
      return;
    }

    const updatedData = {
      technicianEmail: selectedTechnicianUpdate,
      repairID: matchingSchedule.repairID,
    };

    try {
      await updateSchedule({ scheduleId, updatedData }).unwrap();
      toast.success("Schedule updated successfully!");
      handleCloseModal2();
    } catch (error) {
      toast.error(error?.data?.message || "Update failed");
    }
    refetch();
  };

  const userIDD = userByEmial?.user?.userId;
  const [maintenanceData, setMaintenanceData] = useState([]);
  const {
    data: userSchedulesM,
  } = useGetPreventiveSchedulesByUserIDQuery(userIDD, {
    skip: !userIDD,
  });

  useEffect(() => {
    const fetchRepairDetails = async () => {
      if (userSchedulesM && userSchedulesM.length) {
        const repairPromises = userSchedulesM.map(async (schedule) => {
          try {
            const asset = await dispatch(
              maintenanceApiSlice.endpoints.getAssetByAssetCode.initiate(
                schedule?.assetCode,
              ),
            ).unwrap();

            return {
              ...schedule,
              asset,
            };
          } catch (err) {
            console.error(`Error fetching asset for ${schedule?.assetCode}:`, err);
            return null;
          }
        });

        const combinedResults = await Promise.all(repairPromises);
        setMaintenanceData(combinedResults.filter(Boolean));
      }
    };

    fetchRepairDetails();
  }, [userSchedulesM, dispatch]);

  const uniquePriorities = [
    { value: "all", label: "All Priorities" },
    ...Array.from(
      new Set(
        data
          .map((item) => item.repairInfo?.priority)
          .filter(Boolean),
      ),
    ).map((priority) => ({
      value: priority,
      label: priority,
    })),
  ];

  const uniqueWorkStatuses = [
    { value: "all", label: "All Work status" },
    ...Array.from(
      new Set(data.map((item) => item.repairInfo?.status).filter(Boolean)),
    ).map((status) => ({
      value: status,
      label: status,
    })),
  ];

  const searchRecursively = (obj, searchTerm) => {
    if (typeof obj !== "object" || obj === null) {
      return obj?.toString().toLowerCase().includes(searchTerm.toLowerCase());
    }
    return Object.values(obj).some((value) => searchRecursively(value, searchTerm));
  };

  const filteredData = data.filter((item) => {
    const matchesSearch = searchRecursively(item, searchTerm);
    const matchesPriority = selectedPriority === "all" || item.repairInfo?.priority === selectedPriority;
    const matchesWorkStatus = selectedWorkStatus === "all" || item.repairInfo?.status === selectedWorkStatus;
    return matchesSearch && matchesPriority && matchesWorkStatus;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleScheduleView = (item) => {
    setModalData(item);
    setAssignDate(item?.reportingDate);
    setAssignTime(item?.startTime);
  };

  const handleCloseModal = () => {
    setModalData(null);
    setSelectedTechnicianId(null);
    setAssignDate(null);
    setAssignTime(null);
  };

  const handleCloseModal2 = () => {
    setRescheduleModalData(null);
    setSelectedTechnicianUpdate(null);
    setAssignDate(null);
    setAssignTime(null);
    setStatus(null);
  };

  const handleRescheduleView = (item) => {
    setRescheduleModalData(item);
    setSelectedTechnicianUpdate(item?.technicianEmail || "");
    setAssignDate(item?.reportingDate);
    setAssignTime(item?.startTime);
    setStatus(item?.repairInfo?.status);
  };

  const name = userByEmial?.user?.name || "User";
  const userProfileImage = userByEmial?.user?.image || null;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const getFormattedDate = () => {
    const today = new Date();
    const options = { weekday: "long", month: "long", day: "numeric" };
    return today.toLocaleDateString("en-US", options);
  };

  const getInitials = (n) => {
    if (!n) return "U";
    const parts = n.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return n.substring(0, 2).toUpperCase();
  };

  const greeting = getGreeting();
  const formattedDate = getFormattedDate();

  const statsCards = [
    {
      title: "Total Technicians",
      value: filteredUsers?.length || 0,
      icon: Users,
      description: "Available for assignment",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      title: "Work Orders",
      value: maintenanceData?.length || 0,
      icon: ClipboardList,
      description: "Scheduled maintenance",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      title: "Repair Requests",
      value: data?.length || 0,
      icon: Wrench,
      description: "Awaiting supervision",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50/50">
      <div className="p-4 sm:py-6 sm:px-8 space-y-6">
        {/* Welcome Header - Dark Theme Gradient like Manager */}
        <Card className="border-none shadow-lg bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <ShadAvatar className="h-16 w-16 border-2 border-white/20 ring-4 ring-white/10">
                  <AvatarImage src={userProfileImage} alt={name} />
                  <AvatarFallback className="bg-white/10 text-white font-semibold">
                    {getInitials(name)}
                  </AvatarFallback>
                </ShadAvatar>
                <div>
                  <div className="flex items-center gap-2 text-sm text-white/70 mb-1">
                    <Calendar className="h-4 w-4" />
                    <span>{formattedDate}</span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                    {greeting}, {name}!
                  </h1>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border-none shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                    {stat.title}
                  </CardTitle>
                  <div className={`${stat.bgColor} p-2.5 rounded-xl`}>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <p className="text-xs font-medium text-slate-400 mt-1">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Repair Request Table Section */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-bold text-slate-900">Repair Requests</CardTitle>
                <CardDescription className="text-slate-500 mt-1">
                  Manage and assign repair requests to technicians
                </CardDescription>
              </div>
              <TrendingUp className="h-5 w-5 text-slate-400" />
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6 space-y-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search repair requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-56">
                  <ShadSelect
                    value={selectedPriority}
                    onValueChange={(value) => setSelectedPriority(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {uniquePriorities.map((priority) => (
                        <SelectItem key={priority.value} value={priority.value}>
                          {priority.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </ShadSelect>
                </div>
                <div className="w-full sm:w-56">
                  <ShadSelect
                    value={selectedWorkStatus}
                    onValueChange={(value) => setSelectedWorkStatus(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      {uniqueWorkStatuses.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </ShadSelect>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50/50">
                  <TableRow>
                    <TableHead className="w-20 font-bold">Sl. No.</TableHead>
                    <TableHead className="font-bold">Asset Name</TableHead>
                    <TableHead className="font-bold">Date</TableHead>
                    <TableHead className="font-bold">Area</TableHead>
                    <TableHead className="font-bold text-center">Priority</TableHead>
                    <TableHead className="font-bold text-center">Status</TableHead>
                    <TableHead className="font-bold">Description</TableHead>
                    <TableHead className="font-bold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-400 font-medium">
                        No repair requests found matching your criteria.
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedData.map((item, index) => (
                      <TableRow key={item.id || index} className="hover:bg-slate-50/50 transition-colors">
                        <TableCell className="font-medium text-slate-900">
                          {(currentPage - 1) * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell className="font-medium text-slate-700">
                          {item.repairInfo?.assetName || "N/A"}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {item.reportingDate || "N/A"}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {item.repairInfo?.area || "N/A"}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`rounded-full px-3 py-1 border-none font-bold text-[10px] uppercase tracking-wider ${item.repairInfo?.priority === "High"
                            ? "bg-red-100 text-red-600"
                            : item.repairInfo?.priority === "Moderate"
                              ? "bg-amber-100 text-amber-600"
                              : "bg-emerald-100 text-emerald-600"
                            }`}>
                            {item.repairInfo?.priority || "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`rounded-full px-3 py-1 border-none font-bold text-[10px] uppercase tracking-wider ${item.repairInfo?.status === "Completed"
                            ? "bg-emerald-100 text-emerald-600"
                            : item.repairInfo?.status === "In Progress"
                              ? "bg-blue-100 text-blue-600"
                              : "bg-slate-100 text-slate-600"
                            }`}>
                            {item.repairInfo?.status || "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-slate-600">
                          {item.repairInfo?.description || "N/A"}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.repairInfo?.status === "Completed" || item.repairInfo?.status === "In Progress" ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 font-bold text-xs uppercase"
                              onClick={() => !item.technicianEmail ? handleScheduleView(item) : handleRescheduleView(item)}
                            >
                              View Detail
                            </Button>
                          ) : item.technicianEmail ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50 font-bold text-xs uppercase"
                              onClick={() => handleRescheduleView(item)}
                            >
                              Reschedule
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase rounded-lg"
                              onClick={() => handleScheduleView(item)}
                            >
                              Schedule
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between pt-4">
              <p className="text-sm font-medium text-slate-500">
                Found <span className="text-slate-900">{filteredData.length}</span> results
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1.5">
                  {[...Array(totalPages)].map((_, i) => (
                    <Button
                      key={i}
                      variant={currentPage === i + 1 ? "default" : "outline"}
                      size="sm"
                      className={`h-9 w-9 ${currentPage === i + 1 ? "bg-slate-900 text-white" : ""}`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </Button>
                  )).slice(0, 5)}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Modal for schedule Request */}
        <Dialog open={!!modalData} onOpenChange={(open) => !open && handleCloseModal()}>
          <DialogContent className="max-w-md bg-white p-0 overflow-hidden rounded-xl border-none shadow-2xl">
            <DialogHeader className="bg-slate-50 p-6 border-b border-slate-100">
              <DialogTitle className="text-xl font-bold text-slate-900">Schedule Repair</DialogTitle>
              <CardDescription>Assign a technician to this repair request</CardDescription>
            </DialogHeader>
            <div className="p-6 space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">Technician</label>
                <Select
                  options={workerOptions}
                  value={workerOptions?.find(w => w.value === selectedTechnicianId) || null}
                  onChange={(o) => setSelectedTechnicianId(o?.value || "")}
                  placeholder="Select Technician"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "10px",
                      borderColor: "#e2e8f0",
                      padding: "2px",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#cbd5e1" }
                    }),
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Date</label>
                  <Input value={assignDate} readOnly className="bg-slate-50 border-slate-100 rounded-lg h-11" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Time</label>
                  <Input value={assignTime} readOnly className="bg-slate-50 border-slate-100 rounded-lg h-11" />
                </div>
              </div>
            </div>
            <DialogFooter className="p-6 bg-slate-50 border-t border-slate-100">
              <Button
                variant="outline"
                onClick={handleCloseModal}
                className="rounded-lg h-11 font-bold"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSchedule}
                disabled={updating}
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold h-11 px-8 rounded-lg"
              >
                {updating ? "Processing..." : "Confirm Schedule"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal for Reschedule/View Request */}
        <Dialog open={!!rescheduleModalData} onOpenChange={(open) => !open && handleCloseModal2()}>
          <DialogContent className="max-w-md bg-white p-0 overflow-hidden rounded-xl border-none shadow-2xl">
            <DialogHeader className="bg-slate-50 p-6 border-b border-slate-100">
              <DialogTitle className="text-xl font-bold text-slate-900">
                {status === "Pending" ? "Reschedule Request" : "Schedule Details"}
              </DialogTitle>
              <CardDescription>
                {status === "Pending" ? "Modify technician assignment" : "View assigned schedule information"}
              </CardDescription>
            </DialogHeader>
            <div className="p-6 space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">Technician</label>
                <Select
                  options={workerOptions}
                  value={workerOptions?.find(w => w.value === (selectedTechnicianUpdate || rescheduleModalData?.technicianEmail)) || null}
                  onChange={(o) => setSelectedTechnicianUpdate(o?.value || "")}
                  placeholder="Select Technician"
                  isDisabled={status !== "Pending"}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "10px",
                      borderColor: "#e2e8f0",
                      padding: "2px",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#cbd5e1" }
                    }),
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Date</label>
                  <Input value={assignDate} readOnly className="bg-slate-50 border-slate-100 rounded-lg h-11" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Time</label>
                  <Input value={assignTime} readOnly className="bg-slate-50 border-slate-100 rounded-lg h-11" />
                </div>
              </div>
            </div>
            {status === "Pending" && (
              <DialogFooter className="p-6 bg-slate-50 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={handleCloseModal2}
                  className="rounded-lg h-11 font-bold"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReschedule}
                  disabled={updating}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-bold h-11 px-8 rounded-lg"
                >
                  {updating ? "Processing..." : "Confirm Reschedule"}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SupervisorHome;
